import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search as SearchIcon, Loader2, User, Calendar, MapPin, Building2, BookOpen, UtensilsCrossed, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from '../../hooks/useDebounce';
import { searchAll } from '../../lib/search';
import { Avatar } from '../Profile/Avatar';
import type { SearchResult } from '../../types/search';

interface SearchBarProps {
  onResultClick?: () => void;
  onClose?: () => void;
}

export function SearchBar({ onResultClick, onClose }: SearchBarProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get current country from URL parameters
  const currentCountry = searchParams.get('country');

  useEffect(() => {
    const fetchResults = async () => {
      if (!debouncedQuery.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const data = await searchAll(debouncedQuery, currentCountry || undefined);
        setResults(data);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [debouncedQuery]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowResults(false);
        if (!query.trim() && onClose) {
          onClose();
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [query, onClose]);

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'practitioner': return <User className="h-4 w-4" />;
      case 'event': return <Calendar className="h-4 w-4" />;
      case 'venue': return <MapPin className="h-4 w-4" />;
      case 'course': return <BookOpen className="h-4 w-4" />;
      case 'recipe': return <UtensilsCrossed className="h-4 w-4" />;
      case 'user': return <User className="h-4 w-4" />;
    }
  };

  const handleResultClick = (result: SearchResult) => {
    let path = '';
    switch (result.type) {
      case 'practitioner': path = `/practitioners/${result.slug}`; break;
      case 'event': path = `/events/${result.slug}`; break;
      case 'venue': path = `/venues/${result.slug}`; break;
      case 'course': path = `/courses/${result.slug}`; break;
      case 'recipe': path = `/recipes/${result.slug}`; break;
      case 'user': path = `/profile/${result.username}/listings`; break;
    }
    navigate(path);
    setShowResults(false);
    setQuery('');
    onResultClick?.();
  };

  return (
    <div ref={wrapperRef} className="relative w-full max-w-xl mx-auto">
      <div className="relative group flex items-center">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content/30 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowResults(true);
          }}
          onFocus={() => setShowResults(true)}
          placeholder={t('search.placeholder')}
          className="w-full pl-10 pr-10 py-2 bg-background/50 border border-border/50 rounded-lg focus:border-border focus:ring-0 focus:outline-none placeholder:text-content/30 text-sm transition-all hover:border-border/80 backdrop-blur-sm"
          autoFocus
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-content/40" />
        )}
        {query && !loading && (
          <button
            onClick={() => {
              setQuery('');
              setShowResults(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-content/30 hover:text-content/60 transition-colors"
            aria-label={t('search.clearSearch')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {!query && onClose && (
          <button
            onClick={onClose}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-content/30 hover:text-content/60 transition-colors"
            aria-label={t('search.closeSearch')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showResults && (query.trim() || results.length > 0) && (
        <div className="absolute top-full mt-2 w-full bg-background/95 backdrop-blur-md rounded-lg border border-border/50 shadow-lg max-h-[80vh] overflow-y-auto z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          {results.length === 0 ? (
            <div className="p-4 text-content/50 text-center text-sm">
              {loading ? t('search.searching') : t('search.noResults')}
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full p-3 flex items-start space-x-3 hover:bg-accent-base/5 text-left transition-colors"
                >
                  {result.type === 'user' && result.avatar_url ? (
                    <Avatar url={result.avatar_url} size="sm" username={result.username} editable={false} />
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-base/10 flex items-center justify-center text-content/60">
                      {getIcon(result.type)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-content truncate">
                        {result.title}
                      </p>
                      <span className="ml-2 text-xs text-content/40 capitalize">
                        {result.type}
                      </span>
                    </div>
                    {result.description && (
                      <p className="mt-1 text-sm text-content/50 line-clamp-2">
                        {result.description}
                      </p>
                    )}
                    {result.country && (
                      <p className="mt-1 text-xs text-content/40">
                        📍 {result.country}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}